/**
 * Forward Cost Predictor (Patent Sub-system Q)
 * 28-day cost forecast fusing 4 signals: IaC × observability × flags × adoption.
 */
import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { Helmet } from 'react-helmet-async';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Loader2, FlaskConical, TrendingUp, Calendar } from 'lucide-react';
import { toast } from 'sonner';
import DemoDataBanner from '../components/DemoDataBanner';
import BrandTitle from '../components/BrandTitle';

const API_URL = `${process.env.REACT_APP_BACKEND_URL}/api`;
const fmtUsd = (n) => `$${(n || 0).toLocaleString('en-US', { maximumFractionDigits: 0 })}`;

const ForwardPredict = () => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    axios.get(`${API_URL}/forward-predict/demo`)
      .then((r) => setData(r.data))
      .catch(() => toast.error('Failed'))
      .finally(() => setLoading(false));
  }, []);

  const maxCost = data ? Math.max(...data.daily.map(d => d.upper_bound_usd)) : 1;

  return (
    <div className="min-h-screen bg-slate-950 text-slate-100" data-testid="forward-predict-page">
      <Helmet><title>Forward Cost Predictor | CARTIEAI</title></Helmet>
      <div className="max-w-7xl mx-auto px-4 sm:px-6 py-12 sm:py-16">
        <DemoDataBanner />
        <div className="max-w-3xl">
          <div className="inline-flex items-center gap-2 text-[11px] font-mono uppercase tracking-widest text-purple-300 border border-purple-500/30 rounded-md px-2 py-1 mb-5" data-testid="fp-patent-badge">
            <FlaskConical className="w-3 h-3" /> Proprietary engine
          </div>
          <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold text-white tracking-tight leading-[1.05]"><BrandTitle>Forward Cost Predictor</BrandTitle></h1>
          <p className="mt-4 text-base sm:text-lg text-slate-300 leading-relaxed">
            <span className="text-white font-semibold">&quot;You WILL overspend $X next Thursday.&quot;</span>
          </p>
          <p className="mt-3 text-sm text-slate-500">
            Fuses 4 signals: PR diff × observability traffic × feature-flag schedule × product-analytics adoption curve. Daily forecast with 95% prediction interval.
          </p>
        </div>

        {loading && <Loader2 className="w-8 h-8 animate-spin mx-auto my-12 text-slate-500" />}

        {data && (
          <>
            <Card className="mt-10 bg-gradient-to-br from-purple-500/15 to-fuchsia-500/5 border-purple-500/30">
              <CardContent className="p-6 sm:p-8">
                <div className="text-xs uppercase tracking-widest text-purple-300 font-semibold mb-2">Feature: {data.feature_name}</div>
                <div className="text-3xl font-bold text-white" data-testid="fp-total">{fmtUsd(data.total_projected_cost_usd)}</div>
                <div className="text-sm text-slate-300 mt-1">projected total cost over {data.horizon_days} days · 95% PI {fmtUsd(data.total_lower_bound_usd)}–{fmtUsd(data.total_upper_bound_usd)}</div>
                <div className="mt-4 text-sm text-purple-200" data-testid="fp-peak">
                  <Calendar className="w-3.5 h-3.5 inline mr-1" /> Peak: day {data.peak_day_offset} @ {fmtUsd(data.peak_day_cost_usd)}/day
                </div>
              </CardContent>
            </Card>

            <Card className="mt-6 bg-slate-900/60 border border-white/10 shadow-sm border-white/10">
              <CardHeader>
                <CardTitle className="text-white text-lg flex items-center gap-2">
                  <TrendingUp className="w-5 h-5 text-purple-300" /> Daily forecast
                </CardTitle>
                <CardDescription>Each bar shows projected cost; the band = 95% prediction interval.</CardDescription>
              </CardHeader>
              <CardContent>
                <div className="space-y-1" data-testid="fp-daily-chart">
                  {data.daily.map((d) => {
                    const w = (d.projected_cost_usd / maxCost) * 100;
                    const wHigh = (d.upper_bound_usd / maxCost) * 100;
                    return (
                      <div key={d.day_offset} className="flex items-center gap-3 text-xs">
                        <span className="text-slate-500 font-mono w-10 flex-shrink-0">d{d.day_offset}</span>
                        <span className="text-slate-300 font-mono w-12 flex-shrink-0">{d.exposure_pct}%</span>
                        <span className="text-slate-300 font-mono w-12 flex-shrink-0">{d.adoption_pct}%</span>
                        <div className="flex-1 h-4 bg-slate-950 rounded overflow-hidden relative">
                          <div className="h-full bg-purple-400/30" style={{ width: `${wHigh}%` }} />
                          <div className="h-full bg-purple-400 absolute top-0 left-0" style={{ width: `${w}%` }} />
                        </div>
                        <span className="text-white font-mono w-20 text-right font-semibold">{fmtUsd(d.projected_cost_usd)}</span>
                      </div>
                    );
                  })}
                  <div className="flex items-center gap-3 text-[10px] uppercase tracking-widest text-slate-500 mt-3 pt-2 border-t border-white/5">
                    <span className="w-10 flex-shrink-0">day</span><span className="w-12">flag</span><span className="w-12">adopt</span><span className="flex-1">cost (PI)</span><span className="w-20 text-right">$/day</span>
                  </div>
                </div>
              </CardContent>
            </Card>

            <Card className="mt-6 bg-slate-900/60 border border-white/10 shadow-sm border-white/10">
              <CardContent className="p-5">
                <div className="text-xs uppercase tracking-widest text-purple-300 font-semibold mb-2">Rationale</div>
                <p className="text-sm text-slate-200 leading-relaxed" data-testid="fp-rationale">{data.rationale}</p>
              </CardContent>
            </Card>
          </>
        )}
      </div>
    </div>
  );
};

export default ForwardPredict;
